import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlanGateToast } from "@/components/PlanGateToast";
import StarfieldCursor from "@/components/StarfieldCursor";
import InstallPrompt from "@/components/InstallPrompt";
import PWAWelcomeModal from "@/components/PWAWelcomeModal";

import Login from "@/pages/Login";
import TabsLayout from "@/pages/TabsLayout";
import Mirror from "@/pages/Mirror";
import Diary from "@/pages/Diary";
import Symbiosis from "@/pages/Symbiosis";
import Content from "@/pages/Content";
import Library from "@/pages/Library";
import Profile from "@/pages/Profile";
import Paywall from "@/pages/Paywall";
import Planning from "@/pages/Planning";
import Screenwriter from "@/pages/Screenwriter";
import DiaryEntry from "@/pages/DiaryEntry";
import Room from "@/pages/Room";
import LibraryRead from "@/pages/LibraryRead";
import LibraryDaily from "@/pages/LibraryDaily";
import Personalization from "@/pages/Personalization";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";

function RequireAuth({ children }) {
  const { user, loading, justAuthed, consumeJustAuthed } = useAuth();
  const [entranceDone, setEntranceDone] = React.useState(false);
  const shouldPlay = React.useMemo(() => {
    if (!user) return false;
    if (justAuthed) return true;
    try {
      return sessionStorage.getItem("yuo_entrance_shown") !== "1";
    } catch {
      return false;
    }
  }, [user, justAuthed]);
  React.useEffect(() => {
    if (user && shouldPlay && !entranceDone) {
      const t = setTimeout(() => {
        setEntranceDone(true);
        consumeJustAuthed();
        try { sessionStorage.setItem("yuo_entrance_shown", "1"); } catch {}
      }, 2100);
      return () => clearTimeout(t);
    }
  }, [user, shouldPlay, entranceDone, consumeJustAuthed]);
  if (loading) return <SplashLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (shouldPlay && !entranceDone) return <SplashLoader burst />;
  return children;
}

function IndexRedirect() {
  const { user, loading, justAuthed, consumeJustAuthed } = useAuth();
  const [entranceDone, setEntranceDone] = React.useState(false);
  // Play the ceremony on any fresh browser session — using sessionStorage
  // (cleared when tab is closed). This guarantees the entrance always fires
  // after Google callback (where justAuthed=true), and also on a page refresh
  // if the user opens the app in a new tab.
  const shouldPlay = React.useMemo(() => {
    if (!user) return false;
    if (justAuthed) return true;
    try {
      return sessionStorage.getItem("yuo_entrance_shown") !== "1";
    } catch {
      return false;
    }
  }, [user, justAuthed]);
  React.useEffect(() => {
    if (user && shouldPlay && !entranceDone) {
      const t = setTimeout(() => {
        setEntranceDone(true);
        consumeJustAuthed();
        try { sessionStorage.setItem("yuo_entrance_shown", "1"); } catch {}
      }, 2100);
      return () => clearTimeout(t);
    }
  }, [user, shouldPlay, entranceDone, consumeJustAuthed]);
  if (loading) return <SplashLoader />;
  if (user && shouldPlay && !entranceDone) return <SplashLoader burst />;
  return <Navigate to={user ? "/mirror" : "/login"} replace />;
}

/**
 * The universal entrance surface. Renders one YUO dot that pulses gently
 * through the palette. When `burst` is true (post-Google-callback), on
 * the 4th second the dot explodes into full chameleon light and fades —
 * the moment the app opens up.
 */
function SplashLoader({ burst = false }) {
  if (burst) return <WelcomeBreathBurst />;
  return (
    <div
      data-testid="app-splash"
      style={{
        minHeight: "100vh",
        background: "var(--yuo-bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        data-testid="app-splash-dot"
        style={{
          width: 78, height: 78, borderRadius: "50%",
          background: "radial-gradient(38% 34% at 34% 26%, #FFFFFF 0%, #4DFFA0 22%, #0D3A26 60%, #01040A 100%)",
          boxShadow: "0 0 60px 16px rgba(52,255,150,0.28), inset -10px -12px 26px var(--dk-000-9), inset 8px 8px 18px rgba(255,255,255,0.24)",
          animation: "yuo-splash-breath 3.6s ease-in-out infinite",
        }}
      />
      <style>{`
        @keyframes yuo-splash-breath {
          0%,100% { transform: scale(1);    filter: brightness(1); }
          50%     { transform: scale(1.08); filter: brightness(1.2); }
        }
      `}</style>
    </div>
  );
}

function WelcomeBreathBurst() {
  return (
    <div data-testid="app-splash"
      style={{
        minHeight: "100vh", background: "var(--yuo-bg)",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 44,
        fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      }}>
      <div data-testid="app-splash-dot"
        style={{
          width: 140, height: 140, borderRadius: "50%",
          background: "radial-gradient(38% 34% at 34% 26%, #FFFFFF 0%, #4DFFA0 20%, #0D3A26 60%, #01040A 100%)",
          animation: "yuo-welcome-breath 4s ease-in-out infinite",
        }} />
      <div style={{
        fontSize: 15, letterSpacing: "0.6em", textTransform: "uppercase",
        color: "var(--fg-85)",
        animation: "yuo-welcome-word 2.4s cubic-bezier(.2,.7,.2,1) both",
        paddingLeft: "0.6em",
      }}>welcome</div>
      <style>{`
        @keyframes yuo-welcome-breath {
          0%,100% { transform: scale(1);    box-shadow: 0 0 80px 20px rgba(52,255,150,0.24), inset -14px -18px 40px var(--dk-000-9), inset 10px 10px 22px rgba(255,255,255,0.20); }
          50%     { transform: scale(1.06); box-shadow: 0 0 130px 34px rgba(52,255,150,0.36), inset -14px -18px 40px var(--dk-000-9), inset 10px 10px 22px rgba(255,255,255,0.30); }
        }
        @keyframes yuo-welcome-word {
          from { opacity: 0; letter-spacing: 1.4em; filter: blur(10px); }
          to   { opacity: 0.85; letter-spacing: 0.6em; filter: blur(0); }
        }
      `}</style>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <StarfieldCursor />
      <AuthProvider>
        <BrowserRouter>
          <PlanGateToast />
          <Routes>
            <Route path="/" element={<IndexRedirect />} />
            <Route path="/login" element={<Login />} />

            <Route
              element={
                <RequireAuth>
                  <TabsLayout />
                </RequireAuth>
              }
            >
              <Route path="/mirror" element={<Mirror />} />
              <Route path="/diary" element={<Diary />} />
              <Route path="/symbiosis" element={<Symbiosis />} />
              <Route path="/symbiosis/room/:share_id" element={<Symbiosis />} />
              <Route path="/content" element={<Content />} />
              <Route path="/library" element={<Library />} />
            </Route>

            <Route
              path="/profile"
              element={
                <RequireAuth>
                  <Profile />
                </RequireAuth>
              }
            />
            <Route
              path="/planning"
              element={
                <RequireAuth>
                  <Planning />
                </RequireAuth>
              }
            />
            <Route
              path="/paywall"
              element={
                <RequireAuth>
                  <Paywall />
                </RequireAuth>
              }
            />
            <Route
              path="/screenwriter"
              element={
                <RequireAuth>
                  <Screenwriter />
                </RequireAuth>
              }
            />
            <Route
              path="/diary/:id"
              element={
                <RequireAuth>
                  <DiaryEntry />
                </RequireAuth>
              }
            />
            <Route
              path="/library/read"
              element={
                <RequireAuth>
                  <LibraryRead />
                </RequireAuth>
              }
            />
            <Route
              path="/library/daily"
              element={
                <RequireAuth>
                  <LibraryDaily />
                </RequireAuth>
              }
            />
            <Route
              path="/personalization"
              element={
                <RequireAuth>
                  <Personalization />
                </RequireAuth>
              }
            />

            {/* Rooms may be shared publicly, but we still guard behind auth for now */}
            <Route
              path="/room/:shareId"
              element={
                <RequireAuth>
                  <Room />
                </RequireAuth>
              }
            />

            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/billing/cancel" element={<BillingCancel />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <InstallPrompt />
          <PWAWelcomeModal />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

// Register the service worker once the app has painted. In dev CRA disables
// automatic SW registration, so we do it ourselves — this is what enables the
// `beforeinstallprompt` event in Chromium browsers. When the SW file itself
// changes (a new deploy shipped a fresh CACHE_VERSION), we reload the page
// once so the user picks up the new shell immediately instead of seeing
// stale UI on their next visit.
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration) => {
        // Fire an update check every time the tab regains focus.
        window.addEventListener("focus", () => registration.update().catch(() => null));
        registration.addEventListener("updatefound", () => {
          const nextSw = registration.installing;
          if (!nextSw) return;
          nextSw.addEventListener("statechange", () => {
            // A fresh worker just took over — reload to get the new shell.
            if (nextSw.state === "activated" && navigator.serviceWorker.controller) {
              try { window.location.reload(); } catch { /* noop */ }
            }
          });
        });
      })
      .catch((err) => console.warn("[YUO] service worker failed", err));
  });
}

export default App;
